import React, { memo, useCallback, useMemo, useState } from "react";
import { IEFormsLocationPrefill } from "csd.phoenix.models/EFormsLocationPrefill";
import { IconNames } from "@blueprintjs/icons";
import { MenuItem, Button, Label } from "@blueprintjs/core";
import { Select, ItemRenderer, ItemPredicate } from "@blueprintjs/select";
import { getPublicPrefillData, IPublicPrefillData } from "../../stores/formsStore";
import { getCurrentTenantSelection } from "../../services/authService/storage";
import PromiseWait from "./PromiseWait";
import { IFieldOption } from "./form/models";


const SelectItem = Select.ofType<IFieldOption>();

interface IProps {
  onLocationSelected: (locationId: string) => void;
  selectedLocationId: string;
  label?: string;
  showAcceptingFormsOnly?: boolean
  showAllSelection?: boolean;
}

interface IContentProps extends IProps {
  prefillData: IPublicPrefillData;
}


const LocationSelectionContainer: React.FC<IProps> = ({ selectedLocationId, onLocationSelected, label, showAllSelection, showAcceptingFormsOnly}) => {
  const { RecordId: tiid } = getCurrentTenantSelection();
  const promise = useMemo(() => getPublicPrefillData(tiid), [tiid]);

  const mapProps = useCallback(( prefillData: IPublicPrefillData) =>
    ({ prefillData, selectedLocationId, onLocationSelected, label, showAllSelection: showAllSelection, showAcceptingFormsOnly }), [label, onLocationSelected, selectedLocationId, showAcceptingFormsOnly, showAllSelection]);

  return (
    <PromiseWait promise={promise} component={LocationSelection} mapComponentProps={mapProps} />
  )
};


const itemPredicate: ItemPredicate<IFieldOption> = (query, item, _, exact) => {
  const _query = query.toLocaleLowerCase();
  const _text = (item.value).toString().toLowerCase();
  return exact === true
    ? _query === _text
    : _text.indexOf(_query) >= 0;
}

const noResults = <MenuItem text="No match" disabled={true} />;

function getItemsList(locations: IEFormsLocationPrefill[], showAllSelect: boolean) {
  const items: IFieldOption[] = locations!.map(i =>
    ({ value: i.LocationId, text: i.DisplayName}));

  (items.length > 1) && showAllSelect &&  items.splice(0, 0, { value: "", text: "All Locations" });

  return items;
}

const LocationSelection: React.FC<IContentProps> = memo(({ prefillData, onLocationSelected, selectedLocationId, label, showAllSelection, showAcceptingFormsOnly }) => {
  const showAllSelect = showAllSelection ?? true;
  showAcceptingFormsOnly = showAcceptingFormsOnly ?? false;
  const items = useMemo(() => getItemsList(showAcceptingFormsOnly
    ? prefillData.Locations!.filter(l => l.AcceptingForms === true)
    : prefillData.Locations!,
    showAllSelect!),
    [prefillData.Locations, showAcceptingFormsOnly, showAllSelect]);
  selectedLocationId = selectedLocationId.toLocaleLowerCase();

  const [activeItem, setActiveItem]
    = useState<IFieldOption | null>(

      items[!!selectedLocationId
        ? items.findIndex(i => i.value === selectedLocationId)
        : 0] ?? items[0]

    );

  const itemRenderer: ItemRenderer<IFieldOption> = (item, { handleClick, modifiers: { disabled, matchesPredicate, active } }) => {
    return matchesPredicate
      ? <MenuItem
        key={(item.value).toString()}
        text={item.text}
        disabled={disabled}
        active={active}
        onClick={handleClick}
      />
      : null;
  }

  const onActiveItemChange = useCallback((activeItem: IFieldOption) =>
    onLocationSelected(activeItem!.value.toString()), [onLocationSelected]);


  return (
    <Label className="bp3-inline .modifier">
      <strong>{label!}</strong>&nbsp;&nbsp;
       <SelectItem
         items={items}
         activeItem={activeItem}
         onActiveItemChange={setActiveItem}
         onItemSelect={onActiveItemChange}
         itemRenderer={itemRenderer}
         itemsEqual="text"
         itemPredicate={itemPredicate}
         filterable={false}
         noResults={noResults}
       >
         <Button
           text={activeItem?.text}
           rightIcon={IconNames.CHEVRON_DOWN}
         />
       </SelectItem>
    </Label>
  );

})

export default LocationSelectionContainer;
export { LocationSelection };
